import { useState } from "react"
import { X, Copy, Send } from "lucide-react"
import assetWhatsapp from '@/assets/whatsapp.svg'

interface ShareDialogProps {
  isOpen: boolean
  onClose: () => void
  url: string
  title?: string
}


export function ShareDialog({ isOpen, onClose, url, title = "Share" }: ShareDialogProps) {
  const [copied, setCopied] = useState(false)

  if (!isOpen) return null

  // `url` is usually a bare link, but the in-meeting Info panel hands over the
  // whole multi-line invitation, which must keep its line breaks on screen.
  const isMessage = /\s/.test(url.trim())

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Ignore clipboard failure
    }
  }

  const shareOptions = [
    {
      id: "embedded",
      label: "Embedded",
      icon: (
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center dark:bg-muted/50">
          <img src="/icons/embeded.svg" alt="Embedded" className="h-full w-full" />
        </div>
      ),
      onClick: () => {
        // Copy embed code
        const embedCode = `<iframe src="${url}" width="100%" height="600" frameborder="0"></iframe>`
        navigator.clipboard.writeText(embedCode)
        handleCopy()
      }
    },
    {
      id: "whatsapp",
      label: "Whatsapp",
      icon: (
        <div className="h-14 w-14 rounded-full bg-[#25D366] flex items-center justify-center">
          <img src={assetWhatsapp} alt="WhatsApp" className="h-full w-full" />
        </div>
      ),
      onClick: () => {
        const text = encodeURIComponent(`Join my meeting: ${url}`)
        window.open(`https://wa.me/?text=${text}`, "_blank")
      }
    },
    {
      id: "email",
      label: "Email",
      // The Gmail logo belongs to the Gmail tile below. This one opens the
      // device's own mail app, so it gets the same icon the meeting Share
      // dialog uses for Email.
      icon: (
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center dark:bg-muted/50">
          <Send className="h-6 w-6 text-foreground/80" aria-hidden />
        </div>
      ),
      onClick: () => {
        const subject = encodeURIComponent("Meeting Invitation")
        const body = encodeURIComponent(`Join my meeting: ${url}`)
        window.open(`mailto:?subject=${subject}&body=${body}`, "_blank")
      }
    },
    {
      id: "gmail",
      label: "Gmail",
      icon: (
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center dark:bg-muted/50">
          <img src="/icons/gmailEmail.svg" alt="Gmail" className="h-8 w-8" />
        </div>
      ),
      onClick: () => {
        const subject = encodeURIComponent("Meeting Invitation")
        const body = encodeURIComponent(`Join my meeting: ${url}`)
        window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`, "_blank")
      }
    }
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition"
          >
            <X className="h-5 w-5 text-foreground/80" />
          </button>
        </div>

        {/* Share Options */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-4 gap-4">
            {shareOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={option.onClick}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="transition-transform duration-200 group-hover:scale-110">
                  {option.icon}
                </div>
                <span className="text-xs text-foreground font-medium">{option.label}</span>
              </button>
            ))}
          </div>

          {/* URL Display */}
          <div className={`mt-8 flex gap-3 ${isMessage ? "items-start" : "items-center justify-center"}`}>
            {isMessage ? (
              <pre className="flex-1 max-h-60 overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-primary">
                {url}
              </pre>
            ) : (
              <span className="text-sm text-primary font-medium">{url}</span>
            )}
            <button
              type="button"
              onClick={handleCopy}
              className="h-8 w-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-muted transition text-primary"
              title={copied ? "Copied!" : "Copy link"}
            >
              {copied ? (
                <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <Copy className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
